import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Divider,
  Alert,
  Snackbar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip
} from '@mui/material';
import { Send, Preview, PictureAsPdf } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { api } from '../context/AuthContext';

const PLACEHOLDERS = [
  { label: 'Candidate Name', value: '{candidate_name}' },
  { label: 'Job Title', value: '{job_title}' },
  { label: 'Company Name', value: '{company_name}' },
  { label: 'Salary', value: '{salary}' },
  { label: 'Joining Date', value: '{joining_date}' },
];

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ size: ['small', false, 'large', 'huge'] }],
    [{ align: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['blockquote', 'clean'],
  ],
};

const QUILL_FORMATS = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'color', 'background', 'size', 'align',
  'list', 'bullet', 'blockquote',
];

const isHtmlContent = (str) => /<[a-z][\s\S]*>/i.test(str || '');

function applyPlaceholders(content, candidateName, jobTitle, companyName, salary, joiningDate) {
  return content
    .replace(/{candidate_name}/g, candidateName || '[Candidate Name]')
    .replace(/{job_title}/g, jobTitle || '[Job Title]')
    .replace(/{company_name}/g, companyName || '[Company Name]')
    .replace(/{salary}/g, salary || '[Salary]')
    .replace(/{joining_date}/g, joiningDate || '[Joining Date]');
}

const ReleaseOfferLetter = () => {
  const { applicationId } = useParams();
  const navigate = useNavigate();
  const quillRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [releasing, setReleasing] = useState(false);
  const [application, setApplication] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [formData, setFormData] = useState({
    subject: '',
    content: '',
    position: '',
    salary: '',
    joiningDate: new Date().toISOString().split('T')[0],
  });
  const [letterheadUrl, setLetterheadUrl] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [appRes, templatesRes] = await Promise.all([
          api.get(`/applications/${applicationId}`),
          api.get('/offer-letters/templates'),
        ]);
        setApplication(appRes.data);
        setTemplates(Array.isArray(templatesRes.data) ? templatesRes.data : []);
        setFormData(prev => ({
          ...prev,
          position: appRes.data.job.title,
          subject: `Offer Letter: ${appRes.data.job.title} - ExiHire`,
        }));
      } catch (error) {
        console.error('Error fetching data:', error);
        setSnackbar({ open: true, message: 'Failed to fetch application details', severity: 'error' });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [applicationId]);

  const buildContent = (templateContent, salary, joiningDate) => {
    const candidateName = `${application.candidate.firstName} ${application.candidate.lastName}`;
    const companyName = application.job.company_name || application.job.companyName;
    return applyPlaceholders(templateContent, candidateName, application.job.title, companyName, salary, joiningDate);
  };

  const handleTemplateChange = (e) => {
    const templateId = e.target.value;
    setSelectedTemplate(templateId);
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setLetterheadUrl(template.letterhead_url || '');
      setFormData(prev => ({
        ...prev,
        content: buildContent(template.content, prev.salary, prev.joiningDate),
      }));
    } else {
      setLetterheadUrl('');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const next = { ...prev, [name]: value };
      if (selectedTemplate && (name === 'salary' || name === 'joiningDate')) {
        const template = templates.find(t => t.id === selectedTemplate);
        if (template) {
          next.content = buildContent(template.content, next.salary, next.joiningDate);
        }
      }
      return next;
    });
  };

  const handleContentChange = (value) =>
    setFormData(prev => ({ ...prev, content: value }));

  const insertPlaceholder = (placeholder) => {
    const editor = quillRef.current?.getEditor();
    if (editor) {
      const range = editor.getSelection(true);
      const idx = range ? range.index : editor.getLength() - 1;
      editor.insertText(idx, placeholder, 'user');
      editor.setSelection(idx + placeholder.length);
    } else {
      setFormData(prev => ({ ...prev, content: prev.content + placeholder }));
    }
  };

  const handleDownloadPdf = async () => {
    setGeneratingPdf(true);
    try {
      const candidateName = `${application.candidate.firstName} ${application.candidate.lastName}`;
      const response = await api.post(
        '/offer-letters/preview-pdf',
        { content: formData.content, subject: formData.subject, letterheadUrl, candidateName, jobTitle: application.job.title },
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `offer-letter-${application.candidate.lastName || 'candidate'}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating PDF:', error);
      setSnackbar({ open: true, message: 'Failed to generate PDF', severity: 'error' });
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setReleasing(true);
    try {
      await api.post('/offer-letters/release', {
        applicationId,
        ...formData,
        letterheadUrl,
      });
      setSnackbar({ open: true, message: 'Offer letter released successfully!', severity: 'success' });
      setTimeout(() => navigate('/recruiter-applications'), 2000);
    } catch (error) {
      console.error('Error releasing offer letter:', error);
      setSnackbar({ open: true, message: 'Failed to release offer letter', severity: 'error' });
    } finally {
      setReleasing(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const contentIsHtml = isHtmlContent(formData.content);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>Release Offer Letter</Typography>
      <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 4 }}>
        Candidate: {application.candidate.firstName} {application.candidate.lastName} | Job: {application.job.title}
      </Typography>

      <Paper sx={{ p: 4 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Select Template</InputLabel>
                <Select value={selectedTemplate} label="Select Template" onChange={handleTemplateChange}>
                  <MenuItem value=""><em>None</em></MenuItem>
                  {templates.map(t => (
                    <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                name="position"
                label="Job Position"
                fullWidth
                required
                value={formData.position}
                onChange={handleInputChange}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                name="salary"
                label="Offered Salary"
                fullWidth
                required
                value={formData.salary}
                onChange={handleInputChange}
                placeholder="e.g. ₹12,00,000 per annum"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                name="joiningDate"
                label="Joining Date"
                type="date"
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
                value={formData.joiningDate}
                onChange={handleInputChange}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                name="subject"
                label="Email Subject"
                fullWidth
                required
                value={formData.subject}
                onChange={handleInputChange}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>Offer Letter Content</Typography>
              <Box sx={{ mb: 1, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center', mr: 0.5 }}>
                  Insert placeholder:
                </Typography>
                {PLACEHOLDERS.map((p) => (
                  <Tooltip key={p.value} title={`Insert ${p.value}`}>
                    <Chip
                      label={p.label}
                      size="small"
                      variant="outlined"
                      onClick={() => insertPlaceholder(p.value)}
                      sx={{ cursor: 'pointer', fontSize: '0.72rem' }}
                    />
                  </Tooltip>
                ))}
              </Box>
              <Box
                sx={{
                  border: '1px solid rgba(0,0,0,0.23)',
                  borderRadius: 1,
                  '& .ql-editor': { minHeight: 340, fontSize: '14px' },
                  '& .ql-toolbar': { borderBottom: '1px solid rgba(0,0,0,0.15)' },
                  '&:focus-within': { borderColor: 'primary.main', boxShadow: '0 0 0 1px #1976d2' },
                }}
              >
                <ReactQuill
                  ref={quillRef}
                  theme="snow"
                  value={formData.content}
                  onChange={handleContentChange}
                  modules={QUILL_MODULES}
                  formats={QUILL_FORMATS}
                  placeholder="Select a template or write custom content..."
                />
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<Preview />}
                  onClick={() => setPreviewOpen(true)}
                  disabled={!formData.content}
                >
                  Preview
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={releasing ? <CircularProgress size={20} color="inherit" /> : <Send />}
                  disabled={releasing}
                >
                  Release Offer Letter
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Offer Letter Preview</DialogTitle>
        <DialogContent dividers>
          <Box
            sx={{
              width: '100%',
              border: '1px solid #eee',
              boxShadow: '0 0 10px rgba(0,0,0,0.1)',
              backgroundColor: '#fff',
              overflow: 'hidden',
            }}
          >
            {letterheadUrl ? (
              <Box
                component="img"
                src={letterheadUrl}
                alt="Company Letterhead"
                sx={{ width: '100%', height: 'auto', display: 'block' }}
              />
            ) : (
              <Box sx={{ background: 'linear-gradient(135deg, #1E3A8A, #D4AF37)', color: 'white', p: 3, textAlign: 'center' }}>
                <Typography variant="h5">Offer Letter</Typography>
              </Box>
            )}

            <Box sx={{ p: 5, color: '#333' }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                {formData.subject}
              </Typography>
              <Divider sx={{ mb: 3 }} />
              {contentIsHtml ? (
                <Box
                  sx={{
                    fontSize: '1rem',
                    lineHeight: 1.8,
                    '& p': { margin: '0 0 10px' },
                    '& h1': { fontSize: '1.6rem', fontWeight: 700, mb: 1 },
                    '& h2': { fontSize: '1.35rem', fontWeight: 700, mb: 1 },
                    '& h3': { fontSize: '1.15rem', fontWeight: 700, mb: 1 },
                    '& ul, & ol': { paddingLeft: '1.5rem', mb: 1 },
                    '& li': { mb: 0.5 },
                  }}
                  dangerouslySetInnerHTML={{ __html: formData.content }}
                />
              ) : (
                <Box sx={{ whiteSpace: 'pre-wrap', fontSize: '1rem', lineHeight: 1.8 }}>
                  {formData.content}
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
          <Button
            variant="contained"
            startIcon={generatingPdf ? <CircularProgress size={18} color="inherit" /> : <PictureAsPdf />}
            onClick={handleDownloadPdf}
            disabled={generatingPdf || !formData.content}
          >
            {generatingPdf ? 'Generating…' : 'Download PDF'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ReleaseOfferLetter;
